/**
 * Экран достижений, серии и личных рекордов (ТЗ FR-GM-07).
 *
 * Снимок собирается в момент открытия: достижения и серия считаются от
 * состояния пространства, и брать их в момент отрисовки значило бы менять
 * число под рукой у пользователя.
 *
 * Ежедневный план (ТЗ UX-08) — это задания дня и недели (`questProgress`):
 * закрытый и одинаковый для всех список, считаемый по журналу занятий
 * (ТЗ FR-EN-74). План, который нельзя ни спланировать, ни объяснить, понятным
 * не бывает, поэтому случайных заданий здесь нет.
 */

import {
  achievementProgress,
  activeProfile,
  questProgress,
  seasonProgress,
  streakOf,
  type AchievementStatus,
  type QuestStatus,
  type SeasonStatus,
  type Streak,
  type Workspace,
} from '@type2x/feature-personal-workspace';

import type { AppScreen } from './app-state.js';
import type { Runtime } from '../runtime.js';

/** Личные рекорды активного языка. */
export interface PersonalRecords {
  /** Лучшая скорость занятия, знаков в минуту. */
  readonly bestCpm: number;
  /** Лучшая точность занятия, 0..1. */
  readonly bestAccuracy: number;
  /** Лучший подтверждённый результат, слов в минуту. */
  readonly bestVerifiedWpm: number;
  /** Сколько символов набрано за всё время в этом языке. */
  readonly typedCharacters: number;
}

/** Всё, что показывает экран достижений. */
export interface AchievementsView {
  readonly quests: readonly QuestStatus[];
  /** Испытание текущего месяца (ТЗ FR-GM-07). */
  readonly season: SeasonStatus;
  readonly streak: Streak;
  readonly records: PersonalRecords;
  readonly statuses: readonly AchievementStatus[];
}

/** Собирает снимок достижений по состоянию пространства. */
export function achievementsView(workspace: Workspace, nowIso: string): AchievementsView {
  const profile = activeProfile(workspace);

  return {
    quests: questProgress(workspace, nowIso),
    season: seasonProgress(workspace, nowIso),
    // Серия считается по всем языкам: занятие есть занятие, на каком бы
    // языке человек в этот день ни печатал.
    streak: streakOf(
      Object.values(workspace.profiles).flatMap((item) => item.practiceDays),
      nowIso,
    ),
    records: {
      bestCpm: profile.bestCpm,
      bestAccuracy: profile.bestAccuracy,
      bestVerifiedWpm: profile.bestVerifiedWpm,
      typedCharacters: profile.typedCharacters,
    },
    statuses: achievementProgress(workspace),
  };
}

/** Экран достижений. */
export function achievementsScreen(runtime: Runtime, workspace: Workspace): AppScreen {
  return { name: 'achievements', view: achievementsView(workspace, runtime.clock.isoNow()) };
}
